import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from '../../../../../../../components/Modal'
import TextFieldMui from '../../../../../../../components/TextFieldMui'
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { saveTruckTemperature } from '../../../../../../../services/receivement/receivementService'
import { ApiError } from '../../../../../../types'
import * as S from './ModalTruckTemperature.styles'

interface ModalTruckTemperatureProps {
  show: boolean
  onClose: () => void
  receivementId: number
  operatorBadge: string
  testId?: string
}

interface TemperatureData {
  temperature: number
  refrigerationOn: boolean
}

const ModalTruckTemperature: React.FC<ModalTruckTemperatureProps> = ({
  show,
  onClose,
  receivementId,
  operatorBadge,
  testId = 'modal-truck-temperature-test-id',
}) => {
  const intl = useIntl()
  const [temperature, setTemperature] = useState<string>('')
  const [refrigerationOn, setRefrigerationOn] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({})

  const handleClose = (): void => {
    setTemperature('')
    setRefrigerationOn('')
    setValidationErrors({})
    onClose()
  }

  const handleConfirm = async (): Promise<void> => {
    if (temperature && refrigerationOn) {
      setIsLoading(true)
      setValidationErrors({})

      try {
        const temperatureData: TemperatureData = {
          temperature: parseFloat(temperature),
          refrigerationOn: refrigerationOn === '1',
        }

        const result = await saveTruckTemperature(operatorBadge, receivementId, temperatureData)

        if (result.isOk) {
          toast.success(intl.formatMessage({ id: 'truck.temperature.success' }))
          handleClose()
        } else if (result.data && typeof result.data === 'object') {
          const apiError = result.data as ApiError
          if (apiError.validation_errors) {
            setValidationErrors(apiError.validation_errors)
            toast.error(apiError.message || intl.formatMessage({ id: 'truck.temperature.error' }))
          } else {
            toast.error(apiError.message || intl.formatMessage({ id: 'truck.temperature.error' }))
          }
        } else {
          toast.error(intl.formatMessage({ id: 'truck.temperature.error' }))
        }
      } catch (error: any) {
        if (error.response?.data) {
          const apiError = error.response.data as ApiError
          if (apiError.validation_errors) {
            setValidationErrors(apiError.validation_errors)
            toast.error(apiError.message || intl.formatMessage({ id: 'truck.temperature.error' }))
          } else {
            toast.error(apiError.message || intl.formatMessage({ id: 'truck.temperature.error' }))
          }
        } else {
          toast.error(intl.formatMessage({ id: 'truck.temperature.error' }))
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const isFormValid: boolean = Boolean(temperature && refrigerationOn)

  return (
    <Modal
      show={show}
      title={intl.formatMessage({ id: 'truck.temperature.title' })}
      closedButton={false}
      centered
      body={(
        <S.ModalContainer>
          <S.InstructionText>
            <FormattedMessage id='truck.temperature.instruction' />
          </S.InstructionText>

          <S.FieldContainer>
            <TextFieldMui
              name='temperature'
              label='truck.temperature.label'
              type='number'
              value={temperature}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemperature(e.target.value)}
              inputProps={{
                'data-testid': 'temperature-input-test-id',
                min: '-200',
                max: '200',
                step: '0.1',
              }}
              fullWidth
              required
              error={Boolean(validationErrors.temperature)}
              helperText={validationErrors.temperature?.[0]}
            />
          </S.FieldContainer>

          <S.FieldContainer>
            <Typography variant='body1' style={{ marginBottom: '8px', fontWeight: '500' }}>
              <FormattedMessage id='truck.temperature.refrigeration.status' />
            </Typography>
            <FormControl component='fieldset' error={Boolean(validationErrors.refrigeration_on)}>
              <RadioGroup
                value={refrigerationOn}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRefrigerationOn(e.target.value)}
                data-testid='refrigeration-equipment-radio-group-test-id'
                style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
              >
                <FormControlLabel
                  value='1'
                  control={<Radio data-testid='refrigeration-equipment-on-radio-test-id' />}
                  label={<FormattedMessage id='truck.temperature.refrigeration.on' />}
                />
                <FormControlLabel
                  value='0'
                  control={<Radio data-testid='refrigeration-equipment-off-radio-test-id' />}
                  label={<FormattedMessage id='truck.temperature.refrigeration.off' />}
                />
              </RadioGroup>
              {validationErrors.refrigeration_on && (
                <Typography variant='caption' color='error' style={{ marginTop: '4px', display: 'block' }}>
                  {validationErrors.refrigeration_on[0]}
                </Typography>
              )}
            </FormControl>
          </S.FieldContainer>

          <S.ButtonContainer>
            <S.ConfirmButton
              onClick={handleConfirm}
              disabled={!isFormValid || isLoading}
              data-testid='confirm-button-test-id'
            >
              {isLoading ? <FormattedMessage id='truck.temperature.saving' /> : <FormattedMessage id='confirmMovimentation.confirm' />}
            </S.ConfirmButton>
          </S.ButtonContainer>
        </S.ModalContainer>
      )}
      testId={testId}
    />
  )
}

export default ModalTruckTemperature
